import { Icon, Spinner, Typography } from '@/components/common';
import { CardContainer } from '@/components/dashboard';
import Icons from '@/const/icons';
import * as React from 'react';
import { CaseMetricsActionRequired } from './CaseMetricsActionRequired';
import { CaseMetricsClientTarget } from './CaseMetricsClientTarget';
import { CaseMetricsAdvertisingROI } from './CaseMetricsAdvertisingROI';
import { CaseMetricsClientSegmentation } from './CaseMetricsClientSegmentation';
import { CaseMetricsClientAcquisition } from './CaseMetricsClientAcquisition';
import { CaseDetailsLawyer, CaseMetricsData, StatusTypes } from '@/interfaces';
import { SegmentationContentTooltip } from './SegmentationContentTooltip';
import { END_STATUSES_CASE } from '@/const/cases';
import { CaseContext } from '@/context/CaseContext';
import useDeviceWidth from '@/hooks/useDeviceWidth';
import { TooltipComponent } from '@/components/common/tooltip';

interface CaseMetricsProps {
	caseStatus?: StatusTypes;
	caseMetrics?: CaseMetricsData;
	caseLawyerDetails: CaseDetailsLawyer | undefined;
}

export const CaseMetrics: React.FC<CaseMetricsProps> = ({
	caseMetrics,
	caseStatus,
	caseLawyerDetails,
}) => {
	const { isCaseReadOnly } = React.useContext(CaseContext);
	const width = useDeviceWidth();
	const isDocumentRequired = caseLawyerDetails?.is_document_required;
	const isCaseLive = !caseLawyerDetails?.is_draft;

	return (
		<CardContainer
			title="Case Metrics"
			icon={Icons.metricsEmptyState}
			className="!px-4 md:!px-6"
			classNameContent="!h-full"
			classNameTitle="!text-[20px] !leading-[24px]"
			isCaseReadOnly={isCaseReadOnly}
			customButton={
				<div className="flex items-center gap-x-2">
					<div className="flex mt-3 gap-[26px] w-full sm:w-fit items-center rounded-xl bg-green-secondary py-4 pl-6 pr-12 sm:py-[6px] sm:px-[34px] sm:gap-3  sm:mt-0">
						<Icon
							src={Icons.people}
							className="text-grey-60 !w-[35px] !h-[35px]  !min-w-[35px]  !min-h-[35px] sm:!w-6  sm:!h-6 sm:!min-w-6  sm:!min-h-6"
						/>
						<div className="flex flex-col gap-y-1 sm:flex-row  w-full  sm:items-center ">
							<div className="flex justify-between items-center gap-3">
								<Typography
									type="semibold-16"
									className="text-opacity-black !text-[16px] !leading-[19.2px]"
								>
									Participating
								</Typography>
								<Typography
									type="semibold-16"
									className="text-opacity-black !text-[16px] !leading-[19.2px]"
								>
									{caseMetrics?.clientsResult[0]
										?.total_in_progress_or_action_required || '0'}
								</Typography>
							</div>
							<span className="hidden bg-[#0D061666] h-[17px] w-[1px] mx-2 sm:block" />

							<div className="flex justify-between items-center gap-3">
								<Typography
									type="semibold-16"
									className="text-opacity-black !text-[16px] !leading-[19.2px]"
								>
									{caseStatus && END_STATUSES_CASE.includes(caseStatus)
										? 'Canceled'
										: 'On-hold'}
								</Typography>
								<Typography
									type="semibold-16"
									className="text-opacity-black !text-[16px] !leading-[19.2px]"
								>
									{caseMetrics?.clientsResult[0]?.total_on_hold || '0'}
								</Typography>
							</div>
							<span className="hidden bg-[#0D061666] h-[17px] w-[1px] mx-2 sm:block" />

							<div className="flex  justify-between items-center gap-3">
								<Typography
									type="semibold-16"
									className="text-opacity-black !text-[16px] !leading-[19.2px]"
								>
									Dropped off
								</Typography>
								<Typography
									type="semibold-16"
									className="text-opacity-black !text-[16px] !leading-[19.2px]"
								>
									{caseMetrics?.clientsResult[0]?.total_declined_opt_forms ||
										'0'}
								</Typography>
							</div>
						</div>
					</div>
					<TooltipComponent
						className="!w-[300px] sm:!w-[311px]"
						classNameLabel="!text-left"
						align={width < 640 ? 'end' : undefined}
						label={
							<>
								<span className="font-semibold">Participating:</span>
								<br /> Customer has signed a retainer and is actively
								progressing with no blockers.
								<br />
								<br />
								<span className="font-semibold">On-hold:</span>
								<br /> Customer has signed a retainer but is paused due to
								missing required actions.
								<br />
								<br />
								<span className="font-semibold">Dropped off: </span>
								<br />
								Customer signed a retainer but was cancelled for inaction or
								declined by the lawyer later.
							</>
						}
						trigger={
							<div>
								<Icon
									src={Icons.infoCircle}
									className="text-black opacity-60"
									width={20}
									height={20}
								/>
							</div>
						}
					/>
				</div>
			}
		>
			<div className=" flex flex-col  gap-4 lg:grid lg:grid-cols-[688fr_16fr_460fr]">
				<div className="grid grid-cols-1 col-span-2 w-full gap-4">
					<CardContainer
						title="Client Target"
						icon={Icons.profileEmptyState}
						titleTypography="semibold-16"
						className="border border-[#0D061614] px-4 pt-4 pb-7 lg:!p-4"
						classNameContent="!h-fit max-h-[165px] !overflow-hidden"
						classNameTitle="!text-[16px] !leading-[19.2px] truncate break-all text-start]"
					>
						{caseMetrics ? (
							<CaseMetricsClientTarget
								clientTarget={caseMetrics?.ClientTargetResult}
							/>
						) : (
							<div className="flex w-full justify-center items-center my-10">
								<Spinner className="w-8 h-8" />
							</div>
						)}
					</CardContainer>
					<CardContainer
						title="Acquisition Metrics"
						classNameTitle="!text-[16px] !leading-[19.2px] text-center  md:text-start"
						icon={Icons.moneyEmptyState}
						titleTypography="semibold-16"
						className="border border-[#0D061614] px-4 pt-4 pb-7 lg:!p-4"
						classNameContent="!h-fit max-h-[165px] !overflow-hidden"
						tooltipClassName="!w-[300px] sm:!w-[311px]"
						tooltipAlign={width < 640 ? 'end' : undefined}
						tooltipContent={
							<div>
								<Icon
									src={Icons.infoCircle}
									className="text-black opacity-60"
									width={20}
									height={20}
								/>
							</div>
						}
						tooltipLabel={
							<span>
								<span className="font-semibold">
									Cost Per Acquisition (CPA):
								</span>{' '}
								Measures the cost incurred to acquire a client through
								advertising efforts.
								<br />
								<span className="font-semibold">
									Conversion Rate (Submitted to Eligible):
								</span>{' '}
								Reflects the effectiveness of converting clients who have
								submitted claims into eligible clients for the case.
							</span>
						}
					>
						{caseMetrics ? (
							<CaseMetricsAdvertisingROI
								advertisingROI={caseMetrics?.AdvertisingROIResult}
							/>
						) : (
							<div className="flex w-full justify-center items-center my-10">
								<Spinner className="w-8 h-8" />
							</div>
						)}
					</CardContainer>
				</div>
				<div className="grid grid-cols-1 col-span-1">
					<CardContainer
						title="Action Required"
						icon={Icons.exclamationEmptyState}
						classNameTitle="!text-[16px] !leading-[19.2px] truncate break-all text-start"
						titleTypography="semibold-16"
						className="border border-[#0D061614] !p-4"
						classNameContent="!h-fit"
					>
						{caseMetrics ? (
							<CaseMetricsActionRequired
								actionRequired={caseMetrics?.ActionRequiredResult}
							/>
						) : (
							<div className="flex w-full justify-center items-center my-10">
								<Spinner className="w-8 h-8" />
							</div>
						)}
					</CardContainer>
				</div>
				<div className="grid grid-cols-1 col-span-2 w-full">
					<CardContainer
						title="Client Acquisition Funnel"
						classNameTitle="!text-[16px] !leading-[19.2px] max-w-[183px] truncate break-all text-start  lg:max-w-none"
						icon={Icons.clientAcquisition}
						titleTypography="semibold-16"
						className="border border-[#0D061614] px-4 pt-4 pb-7 lg:!p-4"
						classNameContent="!h-fit"
					>
						{caseMetrics ? (
							<CaseMetricsClientAcquisition
								clientAcquisition={caseMetrics?.ClientAcquisitionFunnelResult}
								isDocumentRequired={isDocumentRequired}
								isCaseLive={isCaseLive}
							/>
						) : (
							<div className="flex w-full justify-center items-center my-10">
								<Spinner className="w-8 h-8" />
							</div>
						)}
					</CardContainer>
				</div>
				<div className="grid grid-cols-1 col-span-1">
					<CardContainer
						title="Client Segmentation"
						classNameTitle="!text-[16px] !leading-[19.2px] max-w-[143px] truncate break-all text-start lg:max-w-none"
						icon={Icons.segmentationEmptyState}
						titleTypography="semibold-16"
						className="border border-[#0D061614]  px-4 pt-4 pb-8 lg:!p-4"
						classNameContent="!h-fit"
						tooltipClassName="!w-[300px] sm:!w-[310px]"
						tooltipAlign={width < 640 ? 'end' : undefined}
						tooltipCustomLabel={<SegmentationContentTooltip />}
						tooltipContent={
							<div>
								<Icon
									src={Icons.infoCircle}
									className="text-black opacity-60"
									width={20}
									height={20}
								/>
							</div>
						}
						scroll={false}
					>
						{caseMetrics ? (
							<CaseMetricsClientSegmentation
								clientSegmentation={caseMetrics?.ClientSegmentationResult}
							/>
						) : (
							<div className="flex w-full justify-center items-center my-10">
								<Spinner className="w-8 h-8" />
							</div>
						)}
					</CardContainer>
				</div>
			</div>
		</CardContainer>
	);
};
